/**
 * 付费转化看板 adapter（ADM-MTR-S012）：GET /api/v1/metrics/paywall-conversion 的 snake_case DTO
 * → 页面消费的 camelCase 行，并派生比例 / 占比（分母为 0 或指标不可归因 → null，页面显示 —）。
 *
 * 比例口径（与截图看板一致）：
 * - 进付费面板用户比例 = 进面板 ÷ 活跃；创建付费订单用户比例 = 订单 ÷ 活跃；付费用户比例 = 付费 ÷ 活跃
 * - 创建付费订单用户转化率 = 订单 ÷ 进面板；付费用户转化率 = 付费 ÷ 订单
 * - 占比 = 本行人数 ÷ 汇总行人数
 */

import {
  PAYWALL_CONVERSION_MAX_ROWS,
  type PaywallConversionData,
  type PaywallConversionDimension,
  type PaywallConversionMetrics,
} from '@novel-admin/shared';

export type PaywallConversionMetricsView = {
  activeUsers: number | null;
  paywallUsers: number | null;
  orderUsers: number;
  paidUsers: number;
  avgPayDurationMs: number | null;
  avgPayDurationSamples: number;
  paywallRate: number | null;
  orderRate: number | null;
  paidRate: number | null;
  orderConversion: number | null;
  paidConversion: number | null;
  activeShare: number | null;
  paywallShare: number | null;
  orderShare: number | null;
  paidShare: number | null;
};

export type PaywallConversionRowView = PaywallConversionMetricsView & {
  key: string;
  dims: Partial<Record<PaywallConversionDimension, string | null>>;
};

export type PaywallConversionViewData = {
  window: { from: string; to: string };
  groupBy: PaywallConversionDimension[];
  summary: PaywallConversionMetricsView;
  rows: PaywallConversionRowView[];
  truncated: boolean;
  options: PaywallConversionData['options'];
  revenuePendingReason: string;
  generatedAt: string;
  /**
   * 按支付方式分组、但没有补 0 行的原因（见 `withZeroPaymentChannelRows`）：`truncated` = 服务端结果已截断，
   * 缺席可能只是被截掉；`row_limit` = 数据完整，但补齐会超过行数上限，缺席即为 0。未跳过时无此字段。
   */
  zeroFillSkipped?: 'truncated' | 'row_limit';
};

/** 比率：分子 / 分母任一不可归因或分母为 0 → null（不把「无法计算」显示成 0%） */
export function ratio(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator <= 0) return null;
  return numerator / denominator;
}

function toView(
  metrics: PaywallConversionMetrics,
  summary: PaywallConversionMetrics,
): PaywallConversionMetricsView {
  return {
    activeUsers: metrics.active_users,
    paywallUsers: metrics.paywall_users,
    orderUsers: metrics.order_users,
    paidUsers: metrics.paid_users,
    avgPayDurationMs: metrics.avg_pay_duration_ms,
    avgPayDurationSamples: metrics.avg_pay_duration_samples,
    paywallRate: ratio(metrics.paywall_users, metrics.active_users),
    orderRate: ratio(metrics.order_users, metrics.active_users),
    paidRate: ratio(metrics.paid_users, metrics.active_users),
    orderConversion: ratio(metrics.order_users, metrics.paywall_users),
    paidConversion: ratio(metrics.paid_users, metrics.order_users),
    activeShare: ratio(metrics.active_users, summary.active_users),
    paywallShare: ratio(metrics.paywall_users, summary.paywall_users),
    orderShare: ratio(metrics.order_users, summary.order_users),
    paidShare: ratio(metrics.paid_users, summary.paid_users),
  };
}

export function adaptMetricsPaywallConversion(
  dto: PaywallConversionData,
): PaywallConversionViewData {
  return {
    window: { from: dto.window.from, to: dto.window.to },
    groupBy: dto.group_by,
    summary: toView(dto.summary, dto.summary),
    rows: dto.rows.map((row) => ({
      // 维度值是自由文本（可含任意分隔符），整组 JSON 序列化才能保证 key 唯一且 null 不与字面值相撞
      key: JSON.stringify(dto.group_by.map((dimension) => row.dims[dimension] ?? null)),
      dims: row.dims,
      ...toView(row, dto.summary),
    })),
    truncated: dto.truncated,
    options: dto.options,
    revenuePendingReason: dto.revenue.reason,
    generatedAt: dto.generated_at,
  };
}

/**
 * 按支付方式分组时补齐无数据的结算通道行（显示 0、不隐藏）：服务端只返回有数据的分组，所选范围内某通道
 * （如刚接入的 PayerMax）没有下单 / 到账时整行缺席，运营会误以为该通道没被统计。
 *
 * - 只在 `group_by` 含 `payment_channel` 时补；`truncated=true` 时不补（缺席可能是被截断，补 0 会造假）；
 *   补后超过 `PAYWALL_CONVERSION_MAX_ROWS` 也不补（保持服务端行数上限）
 * - 按其余分组维度的每个已出现组合补齐 `channels` 里缺的通道；全表无行且只按支付方式分组时，补出全部通道
 * - 补出的行人数为 0，活跃 / 进付费面板人数按契约不可归因（null，显示「—」），比率按 0 / 汇总派生
 * - 真有补行时按 `group_by` 逐列重排，近似服务端「各维度 ASC NULLS LAST」（码元序；`null` = 未知在后），
 *   支付方式列与服务端同为字母序，补行与否行序一致；没有补行时原样返回
 * - 因截断或行数上限而没补时在 `zeroFillSkipped` 标出原因，页面据此改写「显示 0」说明
 */
export function withZeroPaymentChannelRows(
  data: PaywallConversionViewData,
  channels: readonly string[],
): PaywallConversionViewData {
  if (!data.groupBy.includes('payment_channel')) return data;
  if (data.truncated) return { ...data, zeroFillSkipped: 'truncated' };
  const otherDims = data.groupBy.filter((dimension) => dimension !== 'payment_channel');
  const comboKeyOf = (dims: PaywallConversionRowView['dims']) =>
    JSON.stringify(otherDims.map((dimension) => dims[dimension] ?? null));
  const combos = new Map<string, PaywallConversionRowView[]>();
  for (const row of data.rows) {
    const key = comboKeyOf(row.dims);
    combos.set(key, [...(combos.get(key) ?? []), row]);
  }
  if (combos.size === 0 && otherDims.length === 0) combos.set(comboKeyOf({}), []);

  const filled: PaywallConversionRowView[] = [];
  for (const comboRows of combos.values()) {
    const sample = comboRows[0];
    const baseDims = Object.fromEntries(
      otherDims.map((dimension) => [dimension, sample?.dims[dimension] ?? null]),
    );
    const present = new Set(comboRows.map((row) => row.dims.payment_channel ?? null));
    for (const channel of channels) {
      if (!present.has(channel)) {
        filled.push(zeroPaymentChannelRow(data, { ...baseDims, payment_channel: channel }));
      }
    }
  }
  if (filled.length === 0) return data;
  if (data.rows.length + filled.length > PAYWALL_CONVERSION_MAX_ROWS) {
    return { ...data, zeroFillSkipped: 'row_limit' };
  }

  const compareValue = (left: string | null | undefined, right: string | null | undefined) => {
    if (left == null || right == null) return Number(left == null) - Number(right == null);
    return left < right ? -1 : left > right ? 1 : 0;
  };
  const rows = [...data.rows, ...filled].sort((left, right) => {
    for (const dimension of data.groupBy) {
      const order = compareValue(left.dims[dimension], right.dims[dimension]);
      if (order !== 0) return order;
    }
    return 0;
  });
  return { ...data, rows };
}

function zeroPaymentChannelRow(
  data: PaywallConversionViewData,
  dims: PaywallConversionRowView['dims'],
): PaywallConversionRowView {
  return {
    key: JSON.stringify(data.groupBy.map((dimension) => dims[dimension] ?? null)),
    dims,
    activeUsers: null,
    paywallUsers: null,
    orderUsers: 0,
    paidUsers: 0,
    avgPayDurationMs: null,
    avgPayDurationSamples: 0,
    paywallRate: null,
    orderRate: null,
    paidRate: null,
    orderConversion: null,
    paidConversion: null,
    activeShare: null,
    paywallShare: null,
    orderShare: ratio(0, data.summary.orderUsers),
    paidShare: ratio(0, data.summary.paidUsers),
  };
}
